import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { cloneDeep } from 'lodash'
import scriptjs from 'scriptjs'

import CkEditorTextbox from '@scoir/components-core/dist/components/CkEditorTextbox'
import CharactersRemaining from '@scoir/components-core/dist/components/CharactersRemaining'

import { Checkbox, FormControlLabel, FormGroup, TableCell, TableRow, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Skeleton } from '@material-ui/lab'

import { ErrorBoundary } from '@scoir/error-reporter'
import SectionTable from './components/SectionTable'
import SkeletonPair from './components/SkeletonPair'
import { LabeledCheckMark, LabeledX } from './components/Icons'

import sanitize from 'common/sanitize-html-helper'
import { getCharCount } from './textUtilities'

const useStyles = makeStyles((theme) => ({
  secondaryText: {
    color: theme.palette.text.secondary,
  },
  exceedsError: {
    color: theme.palette.error.main,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  loadingBox: {
    '& .MuiSkeleton-text': {
      transform: 'unset',
    },
  },
  thirdLevelHeading: {
    color: theme.palette.text.secondary,
  },
  thirdLevelSubHeading: {
    color: theme.palette.text.secondary,
    paddingBottom: theme.spacing(4),
  },
}))

const toolbar = [
  {
    name: 'basicstyles',
    items: [],
  },
  {
    name: 'insert',
    items: ['Link', 'Unlink'],
  },
]

const maxNoteLength = 280

const AdditionalExamsSection = ({ isEditing, applicationConfig, setApplicationConfig, loading, getStyle }) => {
  const classes = useStyles()

  const firstYearConfig = applicationConfig.firstYearConfiguration
  const transferConfig = applicationConfig.transferConfiguration

  const initalVal =
    applicationConfig.additionalExamsNotes && applicationConfig.additionalExamsNotes.body
      ? applicationConfig.additionalExamsNotes.body
      : ''

  const isFYEnabled =
    firstYearConfig && firstYearConfig.profileSections && firstYearConfig.profileSections.additionalExams
  const isTEnabled = transferConfig && transferConfig.profileSections && transferConfig.profileSections.additionalExams
  const showNotes = isFYEnabled || isTEnabled

  const [loadedCkEditor, setLoadedCkEditor] = useState(false)
  useEffect(() => {
    scriptjs(CkEditorTextbox.config.CDN, () => {
      setLoadedCkEditor(true)
    })
  }, [])

  const onChange = (sanitizedVal) => {
    const updatedState = cloneDeep(applicationConfig)
    const isError = validateInput(sanitizedVal)

    const notesConfig = updatedState.additionalExamsNotes ? updatedState.additionalExamsNotes : {}
    notesConfig.body = sanitizedVal
    updatedState.additionalExamsNotes = { ...notesConfig }

    setApplicationConfig(updatedState, isError)
  }

  const validateInput = (val) => {
    const count = getCharCount(val)
    return count > maxNoteLength
  }

  const isError = validateInput(sanitize(initalVal))

  const onToggleProfileSection = (newVal, id, isFirstYear) => {
    const updatedState = cloneDeep(applicationConfig)

    if (isFirstYear) {
      updatedState.firstYearConfiguration.profileSections[id] = newVal
    } else {
      updatedState.transferConfiguration.profileSections[id] = newVal
    }
    setApplicationConfig(updatedState)
  }
  const getReadOnlyView = (configValue, isEnabled) =>
    configValue ? (
      <LabeledCheckMark
        enabled={isEnabled}
        getStyle={getStyle}
      />
    ) : (
      <LabeledX
        enabled={isEnabled}
        getStyle={getStyle}
      />
    )
  const renderProfileSectionConfig = (sectionId, config, firstYearConfig = false) => {
    const isEnabled = config && config.enabled
    const profileSections = config && config.profileSections ? config.profileSections : {}
    return isEditing ? (
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={profileSections[sectionId] ?? false}
              onChange={(e) => onToggleProfileSection(e.target.checked, sectionId, firstYearConfig)}
              name={sectionId}
              color="primary"
              disabled={!isEnabled}
              label="Include"
            />
          }
          label="Include"
        />
      </FormGroup>
    ) : (
      getReadOnlyView(profileSections[sectionId], isEnabled)
    )
  }

  return (
    <SectionTable
      sectionName="Additional Tests"
      getStyle={getStyle}
    >
      <TableRow
        data-testid="profile-section-row"
        className={classes.profileSectionRow}
      >
        <TableCell
          style={{ color: getStyle() }}
          align="left"
          variant="body"
          component="th"
          size="small"
        >
          Include
        </TableCell>
        <TableCell
          align="left"
          size="small"
        >
          {loading && <SkeletonPair />}

          {!loading && renderProfileSectionConfig('additionalExams', firstYearConfig, true)}
        </TableCell>
        <TableCell
          align="left"
          size="small"
        >
          {loading && <SkeletonPair />}
          {!loading && renderProfileSectionConfig('additionalExams', transferConfig)}
        </TableCell>
      </TableRow>
      {showNotes && (
        <TableRow>
          {loading ? (
            <TableCell>
              <Skeleton
                data-testid="loader-test-id"
                className={classes.loadingBox}
                height={337}
              />
            </TableCell>
          ) : isEditing ? (
            <TableCell colSpan={3}>
              <Typography
                variant="body2"
                className={classes.thirdLevelHeading}
              >
                Are there criteria that determine who must submit additional test scores?
              </Typography>
              <Typography
                variant="body2"
                className={classes.thirdLevelSubHeading}
              >
                Use this space to provide details to applicants.
              </Typography>
              {loadedCkEditor ? (
                <React.Fragment>
                  <ErrorBoundary>
                    <CkEditorTextbox
                      initialContent={initalVal}
                      configuration="LINKS"
                      ckEditor={window.CKEDITOR}
                      additionalOptions={{ startupFocus: false, toolbar, versionCheck: false }}
                      onUpdate={(value) => onChange(sanitize(value))}
                    />
                  </ErrorBoundary>

                  <CharactersRemaining
                    textLength={getCharCount(initalVal)}
                    maxChars={maxNoteLength}
                  />

                  {isError && <Typography className={classes.exceedsError}>Exceeds character count.</Typography>}
                </React.Fragment>
              ) : (
                <Skeleton
                  data-testid="loader-test-id"
                  className={classes.loadingBox}
                  height={337}
                />
              )}
            </TableCell>
          ) : (
            <TableCell colSpan={3}>
              <Typography
                variant="body2"
                className={classes.thirdLevelHeading}
              >
                Are there criteria that determine who must submit additional test scores?
              </Typography>
              <Typography
                variant="body2"
                className={classes.thirdLevelSubHeading}
              >
                Use this space to provide details to applicants.
              </Typography>
              {initalVal ? (
                <Typography
                  variant="body1"
                  dangerouslySetInnerHTML={{
                    __html: sanitize(initalVal),
                  }}
                ></Typography>
              ) : (
                <Typography
                  variant="body2"
                  className={classes.secondaryText}
                >
                  None
                </Typography>
              )}
            </TableCell>
          )}
        </TableRow>
      )}
    </SectionTable>
  )
}

AdditionalExamsSection.propTypes = {
  isEditing: PropTypes.bool.isRequired,
  applicationConfig: PropTypes.object.isRequired,
  setApplicationConfig: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  getStyle: PropTypes.func.isRequired,
}

export default AdditionalExamsSection
